/**
 * DomainsGraphView — Step 6
 * ─────────────────────────
 *
 * Domain-coloured asset relationship graph.  Rendered inside the Workbench
 * center pane when the Step 4 view-switcher is set to 'graph'.
 *
 * What it does:
 *   - Loads domain data via the same domainsService path used by DomainsBoardView
 *     (contract first → proposals → none).
 *   - Maps each COBOL program to its domain using majority-vote on paragraph membership
 *     (most paragraphs in a domain wins for that program).
 *   - Renders the existing assetGraph (from CA guided_analyze_asset_graph.json) as a
 *     ReactFlow graph with domain-coloured left-side stripes.
 *   - Cross-domain edges (source and target in different domains) are styled in amber
 *     with a dashed stroke so they stand out clearly.
 *   - A floating legend maps domain colours to domain names.
 *   - Two layout modes: Auto (Dagre LR) and Domain-Grouped (manual column-per-domain).
 *   - Node click highlights all same-domain nodes.
 *
 * Explicitly NOT in Step 6:
 *   - Lasso select + "Assign to domain…" context menu (Step 6 follow-up)
 *   - 10K-node aggregation / viewport culling
 *   - Write-back / domain reassignment from this view
 *
 * Data precedence:
 *   existing contract → AI proposals → uncoloured graph with notice
 *
 * Props:
 *   assetGraph      {object|null}  guided_analyze_asset_graph.json from parent state
 *   scoutAccountId  {string}
 *   applicationName {string}
 *   userId          {string}
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ReactFlow, useNodesState, useEdgesState,
  Background, Controls, Handle, Position, MarkerType,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dagre from '@dagrejs/dagre'
import { Network, Loader2, RefreshCw } from 'lucide-react'

import {
  findDefineDomainsJobId,
  getDomainContract,
  getDomainProposals,
} from '../../services/domainsService.js'

import './DomainsGraphView.css'


// ── Constants ─────────────────────────────────────────────────────────────────

/**
 * 12-colour palette chosen for readability on a dark background and reasonable
 * colour-blind differentiation (avoids pure red/green pairs as signal contrast).
 */
const DOMAIN_PALETTE = [
  '#818cf8', // indigo-400
  '#34d399', // emerald-400
  '#f59e0b', // amber-400
  '#60a5fa', // blue-400
  '#f472b6', // pink-400
  '#a78bfa', // violet-400
  '#2dd4bf', // teal-400
  '#fb923c', // orange-400
  '#4ade80', // green-400
  '#f87171', // red-400
  '#38bdf8', // sky-400
  '#c084fc', // purple-400
]

const UNASSIGNED_COLOR       = '#475569'    // slate — neutral, distinct from domain colours
const CROSS_DOMAIN_COLOR     = '#f59e0b'    // amber — warm, visible, unmistakable
const CROSS_DOMAIN_DASH      = '5 3'

const DEP_TYPE_LABELS = {
  COBOL_PROGRAM: 'Program', COPYBOOK: 'Copybook',
  DB2_TABLE: 'DB2',         DB2_DCLGEN: 'DB2',
  VSAM_DATASET: 'VSAM',     DATA_FILE: 'File',    LOGICAL_FILE: 'File',
  JCL_JOB: 'JCL',           BMS_MAP: 'BMS',
}


// ── Pure helpers ──────────────────────────────────────────────────────────────

/** domainId → palette colour.  Index is stable for a given domain list order. */
function buildDomainColorMap(domains) {
  const map = {}
  domains.forEach((d, i) => { map[d.domain_id] = DOMAIN_PALETTE[i % DOMAIN_PALETTE.length] })
  return map
}

/**
 * programName → domainId using majority-vote.
 * A program whose paragraphs span multiple domains is assigned to the domain
 * that contains the most of its paragraphs.
 */
function buildProgramToDomainId(domains) {
  // Accumulate paragraph counts: { programName: { domainId: count } }
  const counts = {}
  domains.forEach(d => {
    ;(d.members || []).forEach(m => {
      const prog = (m.source_program || '').trim()
      if (!prog) return
      if (!counts[prog]) counts[prog] = {}
      counts[prog][d.domain_id] = (counts[prog][d.domain_id] || 0) + 1
    })
  })
  // Pick winner
  const result = {}
  Object.entries(counts).forEach(([prog, domCounts]) => {
    const [bestDomId] = Object.entries(domCounts).sort((a, b) => b[1] - a[1])[0]
    result[prog] = bestDomId
  })
  return result
}

/** Standard Dagre LR layout — mirrors existing dep-graph behaviour. */
function applyDagreLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'LR', ranksep: 110, nodesep: 60, marginx: 60, marginy: 60 })
  nodes.forEach(n => g.setNode(n.id, { width: 130, height: 50 }))
  edges.forEach(e => g.setEdge(e.source, e.target))
  dagre.layout(g)
  return nodes.map((n, i) => {
    const pos = g.node(n.id)
    const safeX = pos?.x != null && isFinite(pos.x) ? pos.x - 65 : (i % 6) * 170
    const safeY = pos?.y != null && isFinite(pos.y) ? pos.y - 25 : Math.floor(i / 6) * 80
    return { ...n, position: { x: safeX, y: safeY } }
  })
}

/**
 * Domain-grouped layout — each domain gets a vertical column of its programs.
 * Unassigned nodes go in a trailing column.
 * Makes cross-domain edges visually obvious as horizontal crossings.
 */
function applyDomainGroupedLayout(nodes, programToDomainId, domains) {
  const COL_W = 180, ROW_H = 68, PAD_X = 40, PAD_Y = 40

  // Group nodes by domain (by label = asset name)
  const groups = {}                                   // domainId | '__unassigned__' → [node]
  nodes.forEach(n => {
    const domId = programToDomainId[n.data.label] || '__unassigned__'
    if (!groups[domId]) groups[domId] = []
    groups[domId].push(n)
  })

  // Column order: declared domain order, then unassigned
  const domainOrder = domains.map(d => d.domain_id)
  const colOrder = [...domainOrder, '__unassigned__'].filter(id => groups[id]?.length)

  const result = []
  colOrder.forEach((domId, colIdx) => {
    ;(groups[domId] || []).forEach((n, rowIdx) => {
      result.push({ ...n, position: { x: PAD_X + colIdx * COL_W, y: PAD_Y + rowIdx * ROW_H } })
    })
  })
  return result
}


// ── Custom ReactFlow node ─────────────────────────────────────────────────────
// Defined at module level (not inside component) so the reference is stable
// across renders — required by ReactFlow.

function DgvRFNode({ data }) {
  const color     = data.domainColor || UNASSIGNED_COLOR
  const typeLabel = DEP_TYPE_LABELS[data.assetType] || (data.assetType || '')
  return (
    <div className={[
      'dgv-rf-node',
      data.isSelected  ? 'dgv-rf-selected'  : '',
      data.highlighted ? 'dgv-rf-connected' : '',
    ].filter(Boolean).join(' ')}>
      <Handle type="target" position={Position.Left}  style={{ opacity: 0 }} />
      <span className="dgv-rf-stripe" style={{ background: color }} />
      <div className="dgv-rf-body">
        <span className="dgv-rf-name">{data.label}</span>
        <span className="dgv-rf-type" style={{ color }}>{typeLabel}</span>
      </div>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  )
}
const DGV_NODE_TYPES = { dgvAsset: DgvRFNode }


// ── Main component ────────────────────────────────────────────────────────────

export default function DomainsGraphView({ assetGraph, scoutAccountId, applicationName, onSelectionChange, layoutRevisionFromParent = 0, selectedDomainId = null }) {
  const [loadState,   setLoadState]   = useState('loading') // 'loading' | 'ok' | 'error'
  const [dataSource,  setDataSource]  = useState(null)      // 'contract' | 'proposals' | 'none'
  const [domains,     setDomains]     = useState([])
  const [layoutMode,  setLayoutMode]  = useState('dagre')   // 'dagre' | 'domain'
  const [selectedNode, setSelectedNode] = useState(null)
  // ── Step 9: layoutRevision — incrementing this re-applies the current layout
  // algorithm on demand (e.g. after the user has manually dragged nodes).
  const [layoutRevision, setLayoutRevision] = useState(0)

  // ── Step 10: mirror parent-driven layout revision (palette "Re-apply layout") ─
  // Named async function pattern satisfies react-hooks/set-state-in-effect rule.
  useEffect(() => {
    async function applyParentRevision() {
      if (layoutRevisionFromParent > 0) {
        setLayoutRevision(r => r + 1)
      }
    }
    applyParentRevision()
  }, [layoutRevisionFromParent])

  const [rfNodes, setRfNodes, onRfNodesChange] = useNodesState([])
  const [rfEdges, setRfEdges, onRfEdgesChange] = useEdgesState([])


  // ── Load domain data (same precedence as DomainsBoardView) ─────────────────
  // All setState calls are inside the named async function, not in the sync
  // effect body — matches the pattern used by DomainsBoardView to satisfy the
  // react-hooks/set-state-in-effect lint rule.

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!scoutAccountId || !applicationName) {
        if (!cancelled) { setLoadState('ok'); setDataSource('none') }
        return
      }
      setLoadState('loading')
      try {
        const jobId = await findDefineDomainsJobId(scoutAccountId, applicationName)
        if (cancelled) return
        if (!jobId) {
          setDomains([])
          setDataSource('none')
          setLoadState('ok')
          return
        }

        let doms = null
        let src  = null

        // 1. Try saved contract first
        try {
          const contract = await getDomainContract(jobId)
          if (contract?.domains?.length) { doms = contract.domains; src = 'contract' }
        } catch (e) { void e }

        // 2. Fall back to AI proposals
        if (!doms) {
          try {
            const proposals = await getDomainProposals(jobId)
            if (proposals?.domains?.length) { doms = proposals.domains; src = 'proposals' }
          } catch (e) { void e }
        }

        if (!cancelled) {
          setDomains(doms || [])
          setDataSource(src || 'none')
          setLoadState('ok')
        }
      } catch (e) {
        void e
        if (!cancelled) setLoadState('error')
      }
    }
    load()
    return () => { cancelled = true }
  }, [scoutAccountId, applicationName])


  // ── Derive domain maps ──────────────────────────────────────────────────────

  const domainColorMap    = useMemo(() => buildDomainColorMap(domains),    [domains])
  const programToDomainId = useMemo(() => buildProgramToDomainId(domains), [domains])
  const domainNameMap     = useMemo(() => {
    const m = {}
    domains.forEach(d => { m[d.domain_id] = d.name })
    return m
  }, [domains])


  // ── Build ReactFlow nodes + edges ───────────────────────────────────────────
  // Runs when assetGraph, domain maps, or layoutMode changes.
  // All setState calls are inside the inner function so that setSelectedNode
  // does not trigger the react-hooks/set-state-in-effect rule.

  useEffect(() => {
    function rebuild() {
      if (!assetGraph?.assets?.length) {
        setRfNodes([])
        setRfEdges([])
        setSelectedNode(null)
        return
      }

      const assetIds = new Set(assetGraph.assets.map(a => a.id))

      // Asset id → name lookup (for cross-domain edge detection)
      const idToName = {}
      assetGraph.assets.forEach(a => { idToName[a.id] = a.name })

      // Build base nodes (positions are set by layout step below)
      const baseNodes = assetGraph.assets.map(a => {
        const domId    = programToDomainId[a.name] ?? null
        const domColor = domId ? (domainColorMap[domId] ?? UNASSIGNED_COLOR) : null
        // When a domain filter is active, dim nodes outside that domain.
        const inFilter = !selectedDomainId || domId === selectedDomainId
        return {
          id:   a.id,
          type: 'dgvAsset',
          position: { x: 0, y: 0 },
          data: {
            label:       a.name,
            assetType:   a.type,
            domainId:    domId,
            domainColor: domColor,
            highlighted: false,
            isSelected:  false,
          },
          style: { opacity: inFilter ? 1 : 0.15 },
        }
      })

      // Build edges — detect cross-domain
      const rfEdgeList = (assetGraph.relationships || [])
        .filter(r => assetIds.has(r.from) && assetIds.has(r.to))
        .map(r => {
          const fromDom  = programToDomainId[idToName[r.from]] ?? null
          const toDom    = programToDomainId[idToName[r.to]]   ?? null
          const isCross  = fromDom !== null && toDom !== null && fromDom !== toDom
          const color    = isCross ? CROSS_DOMAIN_COLOR : '#475569'
          // Dim edges when neither endpoint belongs to the active domain filter.
          const edgeInFilter = !selectedDomainId || fromDom === selectedDomainId || toDom === selectedDomainId
          return {
            id:     `${r.from}||${r.to}||${r.type}`,
            source: r.from,
            target: r.to,
            type:   'default',
            data:   { isCross, relType: r.type, color },
            style:  {
              stroke:          color,
              strokeWidth:     isCross ? 2 : 1.5,
              strokeDasharray: isCross ? CROSS_DOMAIN_DASH : undefined,
              opacity:         edgeInFilter ? 1 : 0.06,
            },
            markerEnd: { type: MarkerType.ArrowClosed, width: 8, height: 8, color },
          }
        })

      // Apply layout
      const layouted = layoutMode === 'domain'
        ? applyDomainGroupedLayout(baseNodes, programToDomainId, domains)
        : applyDagreLayout(baseNodes, rfEdgeList)

      setRfNodes(layouted)
      setRfEdges(rfEdgeList)
      setSelectedNode(null)
    }
    rebuild()
  }, [assetGraph, programToDomainId, domainColorMap, domainNameMap, domains, layoutMode,
      layoutRevision, selectedDomainId, setRfNodes, setRfEdges])


  // ── Node click: highlight all same-domain nodes ─────────────────────────────

  const onNodeClick = useCallback((event, rfNode) => {
    const nodeId = rfNode.id

    // Toggle off when re-clicking same node
    if (selectedNode === nodeId) {
      setSelectedNode(null)
      setRfNodes(nds => nds.map(n => ({
        ...n,
        data:  { ...n.data, highlighted: false, isSelected: false },
        style: { opacity: !selectedDomainId || n.data.domainId === selectedDomainId ? 1 : 0.15 },
      })))
      setRfEdges(eds => eds.map(e => ({ ...e, style: { ...e.style, opacity: e.data?.edgeInFilter !== false ? 1 : 0.06 } })))
      onSelectionChange?.(null)
      return
    }

    setSelectedNode(nodeId)
    const clickedDomId   = rfNode.data.domainId
    const clickedDomName = clickedDomId ? domainNameMap[clickedDomId] ?? null : null

    setRfNodes(nds => nds.map(n => {
      const sameDom = clickedDomId && n.data.domainId === clickedDomId
      const isThis  = n.id === nodeId
      return {
        ...n,
        data:  { ...n.data, isSelected: isThis, highlighted: sameDom && !isThis },
        style: { opacity: (isThis || sameDom) ? 1 : 0.1 },
      }
    }))
    setRfEdges(eds => eds.map(e => {
      const isAdj = e.source === nodeId || e.target === nodeId
      return { ...e, style: { ...e.style, opacity: isAdj ? 1 : 0.06 } }
    }))

    onSelectionChange?.({
      type: 'asset',
      data: {
        name:        rfNode.data.label,
        assetType:   rfNode.data.assetType,
        domainId:    rfNode.data.domainId,
        domainColor: rfNode.data.domainColor,
        domainName:  clickedDomName,
      },
    })
  }, [selectedNode, selectedDomainId, setRfNodes, setRfEdges, domainNameMap, onSelectionChange])


  // ── Pane click: reset all highlights, respecting active domain filter ────────

  const onPaneClick = useCallback(() => {
    if (!selectedNode) return
    setSelectedNode(null)
    setRfNodes(nds => nds.map(n => ({
      ...n,
      data:  { ...n.data, highlighted: false, isSelected: false },
      // Restore domain-filter opacity rather than always setting to 1.
      style: { opacity: !selectedDomainId || n.data.domainId === selectedDomainId ? 1 : 0.15 },
    })))
    setRfEdges(eds => eds.map(e => {
      const edgeInFilter = !selectedDomainId
        || (e.data?.fromDom === selectedDomainId || e.data?.toDom === selectedDomainId)
      return { ...e, style: { ...e.style, opacity: edgeInFilter ? 1 : 0.06 } }
    }))
    onSelectionChange?.(null)
  }, [selectedNode, selectedDomainId, setRfNodes, setRfEdges, onSelectionChange])


  // ── Render ──────────────────────────────────────────────────────────────────

  if (loadState === 'loading') {
    return (
      <div className="dgv-root dgv-state-center">
        <Loader2 size={16} className="dgv-spin" />
        <span>Loading domain data…</span>
      </div>
    )
  }

  if (!assetGraph?.assets?.length) {
    return (
      <div className="dgv-root dgv-state-center">
        <Network size={28} />
        <span>No asset graph available.</span>
        <span className="dgv-state-sub">
          Run Code Analysis to generate asset relationships, then return here.
        </span>
      </div>
    )
  }

  const crossDomainCount  = rfEdges.filter(e => e.data?.isCross).length
  const hasDomains        = domains.length > 0
  const activeDomainName  = selectedDomainId
    ? (domains.find(d => d.domain_id === selectedDomainId)?.name ?? null)
    : null

  return (
    <div className="dgv-root" id="dc-decomp-panel-graph" role="tabpanel">

      {/* ── Toolbar ────────────────────────────────────────────────────────── */}
      <div className="dgv-toolbar">
        <div className="dgv-toolbar-left">
          <span className="dgv-title">Domain Graph</span>

          {dataSource === 'contract'  && (
            <span className="dgv-tag dgv-tag-contract">Contract</span>
          )}
          {dataSource === 'proposals' && (
            <span className="dgv-tag dgv-tag-proposals">AI Proposals</span>
          )}
          {dataSource === 'none' && (
            <span className="dgv-tag dgv-tag-none">No domain data — nodes uncoloured</span>
          )}
          {activeDomainName && (
            <span className="dgv-tag dgv-tag-filter">
              Filtering: {activeDomainName}
            </span>
          )}
          {crossDomainCount > 0 && (
            <span className="dgv-tag dgv-tag-cross">
              {crossDomainCount} cross-domain {crossDomainCount === 1 ? 'edge' : 'edges'}
            </span>
          )}
        </div>

        <div className="dgv-toolbar-right">
          <div className="dgv-layout-toggle" role="group" aria-label="Layout mode">
            <button
              className={`dgv-layout-btn ${layoutMode === 'dagre' ? 'active' : ''}`}
              onClick={() => setLayoutMode('dagre')}
            >
              Auto Layout
            </button>
            <button
              className={`dgv-layout-btn ${layoutMode === 'domain' ? 'active' : ''}`}
              onClick={() => setLayoutMode('domain')}
              disabled={!hasDomains}
              title={hasDomains ? 'Group nodes by domain' : 'No domain data loaded'}
            >
              Domain Grouped
            </button>
          </div>

          {/* Step 9: Re-apply layout — re-runs the active layout algorithm.
              Useful after manual node dragging has drifted positions. */}
          <button
            className="dgv-clear-btn"
            onClick={() => setLayoutRevision(r => r + 1)}
            title="Re-apply the current layout algorithm to reset node positions"
          >
            <RefreshCw size={12} style={{ marginRight: 4 }} />
            Re-apply layout
          </button>

          {selectedNode && (
            <button className="dgv-clear-btn" onClick={onPaneClick}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Canvas ─────────────────────────────────────────────────────────── */}
      <div className="dgv-canvas-area">
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          onNodesChange={onRfNodesChange}
          onEdgesChange={onRfEdgesChange}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={DGV_NODE_TYPES}
          fitView
          fitViewOptions={{ padding: 0.18 }}
          proOptions={{ hideAttribution: true }}
          minZoom={0.15}
          maxZoom={2.5}
          deleteKeyCode={null}
        >
          <Background color="rgba(139,146,165,0.07)" gap={22} size={1} />
          <Controls showInteractive={false} position="bottom-left" style={{ bottom: 8 }} />
        </ReactFlow>

        {/* Domain legend */}
        {hasDomains && (
          <div className="dgv-legend">
            <div className="dgv-legend-title">Domains</div>
            {domains.map(d => (
              <div key={d.domain_id} className="dgv-legend-item">
                <span className="dgv-legend-swatch" style={{ background: domainColorMap[d.domain_id] }} />
                <span className="dgv-legend-name" title={d.name}>{d.name}</span>
              </div>
            ))}
            {crossDomainCount > 0 && (
              <>
                <div className="dgv-legend-sep" />
                <div className="dgv-legend-item">
                  <span className="dgv-legend-dash-edge" />
                  <span className="dgv-legend-name">Cross-domain edge</span>
                </div>
              </>
            )}
            <div className="dgv-legend-sep" />
            <div className="dgv-legend-item">
              <span className="dgv-legend-swatch" style={{ background: UNASSIGNED_COLOR }} />
              <span className="dgv-legend-name dgv-legend-muted">Unassigned</span>
            </div>
          </div>
        )}

        {/* Floating hint */}
        {!selectedNode && rfNodes.length > 0 && (
          <div className="dgv-hint">
            Click a node to highlight its domain peers
          </div>
        )}
      </div>
    </div>
  )
}
